import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { DEAIChatInstance } from '@/components/deai-chat-instance';
import { SocketProvider } from '@/hooks/socket-context';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/auth-context';

// UUID pattern for detection (standard UUID format)
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Resolution result from the backend
interface ResolveResult {
  shortCode: string;
  venueId: string;
  orgSlug: string;
  serviceSlug: string;
  vanitySlug: string;
  redirect: boolean;
}

export const ChatPage = () => {
  // Extract identifier from URL params (e.g., /talk/:identifier/:vanitySlug)
  // identifier is a short code (12 chars)
  const { identifier, vanitySlug } = useParams<{ identifier: string; vanitySlug: string }>();
  const { language } = useI18n();
  const navigate = useNavigate();
  const { session } = useAuth();

  // State for resolved venue info
  const [resolvedData, setResolvedData] = useState<ResolveResult | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Update document lang attribute for :lang() CSS selectors
  useDocumentLang();

  // Resolve the identifier on mount
  useEffect(() => {
    const resolveIdentifier = async () => {
      if (!identifier) return;

      // Check if identifier is a UUID (needs resolution/potential redirect)
      const isUUID = UUID_PATTERN.test(identifier);

      // For short codes, we can call resolve to get the full mapping
      // For UUIDs, we need to resolve and potentially redirect
      setIsResolving(true);
      setResolveError(null);

      try {
        // Build request with optional auth token for draft mode venues
        const headers: HeadersInit = {};
        if (session?.token) {
          headers['Authorization'] = `Bearer ${session.token}`;
        }
        const response = await fetch(`/api/venues/resolve/${identifier}`, { headers });

        if (!response.ok) {
          if (response.status === 404) {
            setResolveError('Venue not found');
          } else {
            setResolveError('Failed to resolve venue');
          }
          setIsResolving(false);
          return;
        }

        const result: ResolveResult = await response.json();
        console.log('[ChatPage] Resolve result:', result);

        // If redirect is needed (UUID was used), navigate to shortCode URL
        /*
        if (result.redirect && isUUID) {
          console.log('[ChatPage] Redirecting UUID to shortCode URL:', result.shortCode);
          navigate(`/${language}/talk/${result.shortCode}/${result.vanitySlug}`, { replace: true });
          return;
        }
          */

        // Store resolved data for use
        setResolvedData(result);
      } catch (error) {
        console.error('[ChatPage] Error resolving identifier:', error);
        setResolveError('Failed to connect to server');
      } finally {
        setIsResolving(false);
      }
    };

    resolveIdentifier();
  }, [identifier, language, navigate, session?.token]);

  // Show loading state while resolving
  if (isResolving) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (resolveError) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-100">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-red-500 text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Venue Not Found</h1>
          <p className="text-gray-600 mb-6">{resolveError}</p>
          <a
            href={`/${language}`}
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen">
      <SocketProvider>
        <DEAIChatInstance
          venueId={resolvedData?.venueId || identifier}
          vanitySlug={resolvedData?.vanitySlug || vanitySlug}
          shortCode={resolvedData?.shortCode}
          orgSlug={resolvedData?.orgSlug}
        />
      </SocketProvider>

      {/* Version - Bottom Right */}
      <div className="absolute bottom-5 right-5">
        <p className="text-right text-xs md:text-sm">v{import.meta.env.VITE_APP_VERSION}</p>
      </div>
    </div>
  );
};